import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Clock, Users, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const MyRecipesPage = () => {
  const { user, token } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecipes = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API}/recipes?chef_id=${user.id}`);
        setRecipes(response.data);
      } catch (error) {
        console.error("Error fetching recipes:", error);
        toast.error("Erreur lors du chargement des recettes");
      } finally {
        setLoading(false);
      }
    };
    fetchRecipes();
  }, [user]);

  const deleteRecipe = async (recipeId) => {
    try {
      await axios.delete(`${API}/recipes/${recipeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipes(prev => prev.filter(r => r.id !== recipeId));
      toast.success("Recette supprimée");
    } catch (error) {
      console.error("Error deleting recipe:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const publishedRecipes = recipes.filter(r => r.status === "finished");
  const draftRecipes = recipes.filter(r => r.status !== "finished");

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const RecipeCard = ({ recipe, showDelete = true }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="recipe-card overflow-hidden group" data-testid={`my-recipe-${recipe.id}`}>
        <Link to={`/recipe/${recipe.id}`}>
          <div className="aspect-video bg-gradient-to-br from-primary/20 to-accent/30 relative">
            {recipe.final_image ? (
              <img 
                src={recipe.final_image.startsWith('data:') ? recipe.final_image : `data:image/jpeg;base64,${recipe.final_image}`}
                alt={recipe.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <GustoLogo size="lg" />
              </div>
            )}
            <Badge 
              className={`absolute top-2 left-2 ${recipe.status === 'finished' ? 'bg-secondary' : 'bg-accent text-charcoal'}`}
            >
              {recipe.status === 'finished' ? 'Publiée' : 'Brouillon'}
            </Badge>
          </div>
        </Link>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <Link to={`/recipe/${recipe.id}`} className="flex-1">
              <h3 className="font-heading font-semibold text-lg text-charcoal group-hover:text-primary transition-colors">
                {recipe.name}
              </h3>
            </Link>
            {showDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive -mr-2" data-testid={`delete-recipe-${recipe.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer cette recette ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action est irréversible. La recette "{recipe.name}" sera définitivement supprimée.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => deleteRecipe(recipe.id)}
                      className="rounded-full bg-destructive hover:bg-destructive/90"
                    >
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {(recipe.prep_time || 0) + (recipe.cook_time || 0)} min
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {recipe.servings} parts
            </span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <div className="px-4 py-6 max-w-7xl mx-auto" data-testid="my-recipes-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-charcoal">Mes Recettes</h1>
        <Link to="/record">
          <Button className="bg-primary hover:bg-primary/90 rounded-full" data-testid="new-recipe-btn">
            <Plus className="w-5 h-5 mr-2" />
            Nouvelle recette
          </Button>
        </Link>
      </div>

      <Tabs defaultValue="published" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
          <TabsTrigger value="published" className="rounded-full" data-testid="tab-published">
            Publiées ({publishedRecipes.length})
          </TabsTrigger>
          <TabsTrigger value="drafts" className="rounded-full" data-testid="tab-drafts">
            Brouillons ({draftRecipes.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="published">
          {publishedRecipes.length === 0 ? (
            <Card className="p-8 text-center">
              <GustoLogo size="lg" />
              <p className="text-muted-foreground">Aucune recette publiée</p>
              <Link to="/record">
                <Button className="mt-4 rounded-full bg-primary" data-testid="create-first-recipe-btn">
                  Créer ma première recette
                </Button>
              </Link>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publishedRecipes.map(recipe => (
                <RecipeCard key={recipe.id} recipe={recipe} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="drafts">
          {draftRecipes.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">Aucun brouillon</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {draftRecipes.map(recipe => (
                <RecipeCard key={recipe.id} recipe={recipe} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyRecipesPage;
